import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import {
  paymentScheduleTemplateEndpoints,
  paymentSchedulePresetEndpoints,
} from '@/lib/api/endpoints';
import { paymentScheduleTemplateKeys, paymentSchedulePresetKeys } from '@/lib/queryKeys';
import type { PublicPaymentPlanPreview } from '@/components/public/PaymentPlanPreview';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';

export type ScheduleTrigger =
  | 'ON_ESTIMATE_ACCEPTED'
  | 'ON_JOB_SCHEDULED'
  | 'ON_JOB_STARTED'
  | 'ON_MILESTONE_COMPLETE'
  | 'ON_JOB_COMPLETED'
  | 'DAYS_AFTER_COMPLETION'
  | 'FIXED_DATE';

export type ScheduleAction =
  | 'CREATE_DEPOSIT_INVOICE'
  | 'CREATE_INVOICE'
  | 'AUTO_CHARGE'
  | 'REQUEST_PAYMENT_ONLY';

export interface ScheduleAmountSpec {
  kind: 'percent_of_job_total' | 'fixed_amount' | 'remaining_balance';
  value?: number;
  daysAfter?: number;
  fixedDate?: string;
}

export interface ScheduleRule {
  trigger: ScheduleTrigger;
  amountSpec: ScheduleAmountSpec;
  action: ScheduleAction;
  autoCharge?: boolean;
  milestoneRef?: string;
}

export interface PaymentScheduleTemplate {
  id: string;
  status: 'DRAFT' | 'LOCKED' | 'INSTANTIATED' | 'VOIDED';
  consentRequired: boolean;
  previewText: string | null;
  rules: Array<ScheduleRule & { id: string; position: number }>;
}

export interface PaymentSchedulePreset {
  id: string;
  tenantId: string | null;
  name: string;
  description: string | null;
  rules: ScheduleRule[];
  isSystemPreset: boolean;
}

function refreshEstimatePaymentSchedule(qc: ReturnType<typeof useQueryClient>, estimateId: string) {
  return Promise.all([
    qc.invalidateQueries({
      queryKey: paymentScheduleTemplateKeys.forEstimate(estimateId),
    }),
    qc.invalidateQueries({
      queryKey: paymentScheduleTemplateKeys.preview(estimateId),
    }),
  ]);
}

interface UsePaymentScheduleTemplateOptions {
  enabled?: boolean;
}

export function usePaymentScheduleTemplate(
  estimateId: string | undefined,
  options: UsePaymentScheduleTemplateOptions = {},
) {
  return useApiQuery<PaymentScheduleTemplate | null>({
    queryKey: estimateId ? paymentScheduleTemplateKeys.forEstimate(estimateId) : ['psTpl', 'none'],
    queryFn: async () =>
      apiGet(operationsClient, paymentScheduleTemplateEndpoints.forEstimate(estimateId as string)),
    enabled: Boolean(estimateId) && (options.enabled ?? true),
    showErrorToast: false,
  });
}

/**
 * `audience: 'staff'` opts out of the tenant's `showPaymentPlanPreview` setting,
 * which is customer-facing copy. Default to the customer view: anything that
 * reaches the customer (the PDF, the signed document) must honour the setting,
 * and defaulting the other way silently publishes a plan the tenant hid.
 */
export function useTemplatePreview(
  estimateId: string | undefined,
  options: { audience?: 'staff' } = {},
) {
  const { audience } = options;
  return useApiQuery<PublicPaymentPlanPreview | null>({
    queryKey: estimateId
      ? [...paymentScheduleTemplateKeys.preview(estimateId), audience ?? 'customer']
      : ['psTpl', 'preview', 'none'],
    queryFn: async () =>
      apiGet(
        operationsClient,
        paymentScheduleTemplateEndpoints.preview(estimateId as string, audience),
      ),
    enabled: Boolean(estimateId),
    showErrorToast: false,
  });
}

export function useAttachScheduleTemplate(estimateId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (input: { rules: ScheduleRule[]; previewText?: string }) =>
      apiPost(operationsClient, paymentScheduleTemplateEndpoints.forEstimate(estimateId), input),
    onSuccess: () => refreshEstimatePaymentSchedule(qc, estimateId),
  });
}

export function usePatchScheduleTemplate(estimateId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (input: { rules?: ScheduleRule[]; previewText?: string }) =>
      apiPatch(operationsClient, paymentScheduleTemplateEndpoints.forEstimate(estimateId), input),
    onSuccess: () => refreshEstimatePaymentSchedule(qc, estimateId),
  });
}

export function useDeleteScheduleTemplate(estimateId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async () =>
      apiDelete(operationsClient, paymentScheduleTemplateEndpoints.forEstimate(estimateId)),
    onSuccess: () => refreshEstimatePaymentSchedule(qc, estimateId),
  });
}

interface UseSchedulePresetsOptions {
  enabled?: boolean;
}

export function useSchedulePresets(options: UseSchedulePresetsOptions = {}) {
  return useApiQuery<PaymentSchedulePreset[]>({
    queryKey: paymentSchedulePresetKeys.list(),
    queryFn: async () => apiGet(operationsClient, paymentSchedulePresetEndpoints.list()),
    enabled: options.enabled ?? true,
    showErrorToast: false,
  });
}

interface CreateSchedulePresetInput {
  name: string;
  description?: string;
  rules: ScheduleRule[];
}

/** NOT-229: save the current builder ruleset as a tenant-custom preset (§9). */
export function useCreateSchedulePreset() {
  const queryClient = useQueryClient();
  return useApiMutation<PaymentSchedulePreset, CreateSchedulePresetInput>({
    mutationFn: (input) =>
      apiPost(operationsClient, paymentSchedulePresetEndpoints.create(), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentSchedulePresetKeys.list() });
    },
  });
}

/** NOT-229: soft-delete a tenant-custom preset (system presets are protected). */
export function useDeleteSchedulePreset() {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete(operationsClient, paymentSchedulePresetEndpoints.delete(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentSchedulePresetKeys.list() });
    },
  });
}
